import { useEffect, useMemo, useState } from 'react';
import NewHeroSection from '@/pages/home/components/NewHeroSection';
import ReviewSection from '@/pages/home/components/ReviewSection';
import MainQuestionSection from '@/pages/home/components/MainQuestionSection';
import CategoryPartnerShops from '@/pages/home/components/CategoryPartnerShops';
import type { HomeCategoryTabKey } from '@/pages/home/constants';
import {
  getMainPartnerShopsByCategory,
  getMainReviews,
  type MainPartnerShopCategoryMap,
  type MainReviewsData,
} from '@/api/main';

const initialReviewQuotes: string[] = [
  '원단 차이를 하나씩 설명해줘서 고르기가 훨씬 쉬웠어요.',
  '예약했을 때부터 결혼식까지 꼼꼼하게 봐줘서 챙길 게 많았어요.',
  '촬영복 구성이 다양해서 여러 분위기로 찍어볼 수 있었어요.',
  '다이아몬드도 상태가 깔끔해서 설명 때 걱정 없이 믿었어요.',
  '혼식 때 오려 입어도 불편하지 않았고 사진에서도 잘 나왔어요.',
  '비싼 것만 권하지 않고 원하는 스타일 기준으로 봐줘서 편했어요.',
];

const heroImages = [
  {
    src: 'https://cafe-article-raw-images.s3.ap-northeast-2.amazonaws.com/naver-cafe/articles/25228091/8879729/images/058d628b29b0-w720.jpg',
    alt: '예복 피팅 후기 사진',
  },
  {
    src: 'https://cafe-article-raw-images.s3.ap-northeast-2.amazonaws.com/naver-cafe/articles/25228091/8976291/images/af3d09173de3-w720.jpg',
    alt: '원단 상담 후기 사진',
  },
  {
    src: 'https://cafe-article-raw-images.s3.ap-northeast-2.amazonaws.com/naver-cafe/articles/25228091/9009738/images/39bf9af57602-w720.jpg',
    alt: '촬영 예복 후기 사진',
  },
  {
    src: 'https://cafe-article-raw-images.s3.ap-northeast-2.amazonaws.com/naver-cafe/articles/25228091/8894517/images/ffa0da58faec-w720.jpg',
    alt: '수트 후기 사진',
  },
];

const partnerShopSections: Array<{
  key: HomeCategoryTabKey;
  title: string;
  description: string;
  listPath: string;
  emptyMessage: string;
}> = [
  {
    key: 'suit',
    title: '후기로 검증된 예복 인증업체',
    description: '핏, 원단, 촬영복 구성까지 실제 후기로 확인해보세요.',
    listPath: '/shops/suit',
    emptyMessage: '예복 인증업체를 불러오는 중입니다.',
  },
  {
    key: 'jewelry',
    title: '후기로 검증된 예물 인증업체',
    description: '다이아, 커플링, 예물 구성 기준을 업체별 후기와 함께 비교해보세요.',
    listPath: '/shops/jewelry',
    emptyMessage: '예물 인증업체는 곧 업데이트될 예정이에요.',
  },
  {
    key: 'hanbok',
    title: '후기로 검증된 한복 인증업체',
    description: '신랑신부 한복부터 혼주 한복까지 실제 사진과 후기로 살펴보세요.',
    listPath: '/shops/hanbok',
    emptyMessage: '한복 인증업체는 곧 업데이트될 예정이에요.',
  },
];

export default function Home() {
  const [activeReviewTab, setActiveReviewTab] = useState<HomeCategoryTabKey>('suit');
  const [reviewsByCategory, setReviewsByCategory] = useState<MainReviewsData>({
    suit: {
      shopCategory: '예복',
      reviewQuotes: initialReviewQuotes,
      reviewImages: [],
    },
  });
  const [partnerShopsByCategory, setPartnerShopsByCategory] = useState<MainPartnerShopCategoryMap>({});
  const [partnerShopsLoading, setPartnerShopsLoading] = useState(true);

  const activeReviewData = useMemo(() => {
    return reviewsByCategory[activeReviewTab] || {
      shopCategory: '',
      reviewQuotes: [],
      reviewImages: [],
    };
  }, [activeReviewTab, reviewsByCategory]);

  useEffect(() => {
    let ignore = false;

    async function fetchMainReviews() {
      try {
        const result = await getMainReviews();

        if (!ignore) {
          setReviewsByCategory((prev) => ({
            ...prev,
            ...result,
          }));
        }
      } catch (error) {
        console.error('[Home] failed to fetch main reviews', error);
      }
    }

    fetchMainReviews();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    let ignore = false;

    async function fetchMainPartnerShopsByCategory() {
      setPartnerShopsLoading(true);

      try {
        const result = await getMainPartnerShopsByCategory();

        if (!ignore) {
          setPartnerShopsByCategory(result);
        }
      } catch (error) {
        console.error('[Home] failed to fetch main partner shops by category', error);
      } finally {
        if (!ignore) {
          setPartnerShopsLoading(false);
        }
      }
    }

    fetchMainPartnerShopsByCategory();

    return () => {
      ignore = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-bg-normal">
      <NewHeroSection images={heroImages} />
      <MainQuestionSection />
      {/* <ReviewSection
        activeTab={activeReviewTab}
        reviewQuotes={activeReviewData.reviewQuotes}
        reviewImages={activeReviewData.reviewImages}
        onTabChange={setActiveReviewTab}
      /> */}
      <div className="bg-white">
        {partnerShopSections.map((section) => (
          <CategoryPartnerShops
            key={section.key}
            title={section.title}
            description={section.description}
            shops={partnerShopsByCategory[section.key]?.list || []}
            listPath={section.listPath}
            loading={partnerShopsLoading}
            emptyMessage={section.emptyMessage}
          />
        ))}
      </div>
    </div>
  );
}
